"use client"

import { useMemo, useState } from "react"
import { AlertTriangle, ClipboardList, FileText, LayoutGrid, Users } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import { CAST, CAST_BY_ID } from "@/modules/lernpfad/data/cast"
import { DEMO_CATALOG } from "@/modules/lernpfad/data/obligations"
import { composeCurriculum, computeDueObligations } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { REASON_VAR, REASON_ORDER } from "./charts/palette"
import TeamStatusStrip, { type CellState, type TeamStatusRow } from "./charts/team-status-strip"
import CurriculumItemCard from "./curriculum-item-card"
import { SectionHeading } from "./ui-bits"
import TransferQueue from "./wirkung/transfer-queue"
import { ViewHeader } from "@/modules/lernpfad/components/ui-bits"

/** The manager the demo uses. Andrea leads Rayon 3. */
const MANAGER_ID = "cast-vogt"

export default function ManagerView({ state }: { state: LernpfadDemoState }) {
    const { constraints, asOf, localizer, rationaleStrings } = state
    const { t } = localizer
    const manager = CAST_BY_ID.get(MANAGER_ID)!
    const team = useMemo(() => CAST.filter((member) => member.managerId === MANAGER_ID), [])
    const [selectedId, setSelectedId] = useState(team[0]?.id ?? "")

    // Each report's plan under the CURRENT levers — the manager view is the
    // same engine, not a second implementation.
    const rows = useMemo(
        () =>
            team.map((member) => ({
                member,
                curriculum: composeCurriculum(member, DEMO_CATALOG, constraints),
                due: computeDueObligations(member, DEMO_CATALOG, constraints),
            })),
        [team, constraints],
    )

    const selected = rows.find((row) => row.member.id === selectedId) ?? rows[0]

    /** Person × duty matrix: compliant / due / overdue / not applicable. */
    const statusRows: TeamStatusRow[] = useMemo(
        () =>
            rows.map(({ member, due }) => {
                const cells: Record<string, CellState> = {}
                for (const obligation of DEMO_CATALOG.obligations) {
                    const binds =
                        obligation.appliesToRoles.includes(member.roleId) ||
                        (obligation.appliesToSigningAuthority &&
                            member.signingAuthority &&
                            obligation.appliesToSigningAuthority.includes(member.signingAuthority))
                    if (!binds) {
                        cells[obligation.id] = "n/a"
                        continue
                    }
                    const entry = due.find((candidate) => candidate.obligation.id === obligation.id)
                    cells[obligation.id] = !entry ? "ok" : entry.overdue ? "overdue" : "due"
                }
                return { id: member.id, name: member.displayName, cells }
            }),
        [rows],
    )

    const peakHours = Math.max(...rows.map((row) => row.curriculum.totalHours), 1)
    const artefacts = selected ? localizer.artefacts(selected.member.id, selected.member.artefacts) : undefined

    return (
        <div className="space-y-8">
            <ViewHeader
                title={t("manager.title")}
                subtitle={
                    <>
                        {manager.displayName} · {manager.orgUnitLabel} ·{" "}
                        {t("manager.subtitle", { count: team.length })}
                    </>
                }
            />

            <div className="flex items-start gap-3 rounded-lg border-l-4 border-l-primary border-y border-r bg-muted/30 p-4 text-sm">
                <Users className="mt-0.5 size-4 shrink-0 text-primary" />
                <p className="text-muted-foreground">{t("manager.disclaimer")}</p>
            </div>

            {/* Compliance matrix */}
            <section className="lp-card rounded-xl border bg-card p-5">
                <SectionHeading
                    icon={LayoutGrid}
                    title={t("manager.matrix")}
                    action={
                        <span className="text-xs text-muted-foreground">{t("manager.matrixHint")}</span>
                    }
                />
                <TeamStatusStrip
                    rows={statusRows}
                    obligations={DEMO_CATALOG.obligations}
                    t={t}
                    obligationLabel={localizer.obligationLabel}
                    onSelect={setSelectedId}
                    selectedId={selected?.member.id}
                />
            </section>

            {/* Effort per report, stacked by reason */}
            <section className="lp-card rounded-xl border bg-card p-5">
                <TransferQueue state={state} team={team} />
            </section>

            <section>
                <SectionHeading icon={ClipboardList} title={t("manager.effort")} />
                <div className="space-y-1">
                    {rows.map(({ member, curriculum, due }) => {
                        const overdue = due.filter((entry) => entry.overdue).length
                        const isSelected = member.id === selected?.member.id
                        return (
                            <button
                                key={member.id}
                                type="button"
                                onClick={() => setSelectedId(member.id)}
                                className={cn(
                                    "w-full rounded-md px-2 py-1.5 text-left transition-colors",
                                    isSelected ? "bg-muted/70" : "hover:bg-muted/40",
                                )}
                            >
                                <div className="flex items-baseline justify-between gap-3 text-sm">
                                    <span className="flex min-w-0 items-center gap-2">
                                        <span className="truncate font-medium">{member.displayName}</span>
                                        <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                                            {localizer.role(member.roleId, member.roleLabel)} ·{" "}
                                            {member.pensum}%
                                        </span>
                                    </span>
                                    <span className="flex shrink-0 items-center gap-2 text-xs tabular-nums text-muted-foreground">
                                        {overdue > 0 && (
                                            <span className="inline-flex items-center gap-1 font-semibold text-destructive">
                                                <AlertTriangle className="size-3" />
                                                {t("manager.overdueBadge", { count: overdue })}
                                            </span>
                                        )}
                                        {t("item.hours", { hours: curriculum.totalHours })}
                                    </span>
                                </div>
                                {/* Stacked by reason, so composition is visible not just size. */}
                                <div className="mt-1 flex h-2 gap-[2px] overflow-hidden rounded-full">
                                    {REASON_ORDER.map((reason) => {
                                        const hours = curriculum.items
                                            .filter((item) => item.reason === reason)
                                            .reduce((sum, item) => sum + item.baustein.durationHours, 0)
                                        if (hours === 0) return null
                                        return (
                                            <span
                                                key={reason}
                                                style={{
                                                    width: `${(hours / peakHours) * 100}%`,
                                                    background: REASON_VAR[reason],
                                                }}
                                            />
                                        )
                                    })}
                                </div>
                            </button>
                        )
                    })}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t pt-2">
                    {REASON_ORDER.map((reason) => (
                        <span
                            key={reason}
                            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
                        >
                            <span
                                className="size-2 rounded-[2px]"
                                style={{ background: REASON_VAR[reason] }}
                            />
                            {localizer.reason(reason)}
                        </span>
                    ))}
                </div>
            </section>

            {/* Selected person's proposal */}
            {selected && (
                <section>
                    <SectionHeading
                        icon={FileText}
                        title={t("manager.proposalFor", { name: selected.member.displayName })}
                        action={
                            <Button size="sm" variant="outline">
                                {t("manager.adopt")}
                            </Button>
                        }
                    />
                    <div className="space-y-2.5">
                        {selected.curriculum.items.map((item) => (
                            <CurriculumItemCard
                                key={item.baustein.id}
                                item={item}
                                employee={selected.member}
                                asOf={asOf}
                                localizer={localizer}
                                rationaleStrings={rationaleStrings}
                                compact
                            />
                        ))}
                        {selected.curriculum.items.length === 0 && (
                            <p className="text-sm text-muted-foreground">{t("manager.noAction")}</p>
                        )}
                    </div>

                    {artefacts?.mag && (
                        <details className="mt-4 rounded-lg border bg-muted/25 p-4">
                            <summary className="cursor-pointer text-sm font-medium">
                                {t("manager.basis")}
                            </summary>
                            <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">
                                {artefacts.mag}
                            </pre>
                        </details>
                    )}
                </section>
            )}
        </div>
    )
}
